<template>
  <div
    class="moods tw:grid tw:grid-cols-5 tw:gap-1.5"
    :class="{ 'moods--stacked': stacked }"
    role="group"
    :aria-label="t('nkf.mood')"
  >
    <button
      v-for="option in items"
      :key="option.value"
      type="button"
      :disabled="readonly"
      class="mood-choice tw:flex tw:flex-col tw:items-center tw:text-(--muted-foreground) tw:type-micro tw:gap-1.25 tw:py-1.5 tw:px-1 tw:border-[1.5px] tw:border-solid tw:border-transparent tw:rounded-(--control-radius)"
      :style="{ '--mood-background': option.background }"
      :aria-pressed="level === option.value"
      @click="level = option.value"
    >
      <img v-if="option.photo" :src="option.photo" alt="" :width="faceSize" :height="faceSize" />
      <MoodFace v-else :level="option.level ?? option.value" :size="faceSize" />
      <span class="tw:wrap-anywhere">{{ option.label }}</span>
      <span
        v-if="stacked"
        class="mood-choice__radio tw:grid tw:place-items-center tw:flex-none tw:text-primary-foreground tw:w-6.5 tw:h-6.5 tw:border-[2.5px] tw:border-solid tw:border-(--border-button) tw:rounded-[50%] tw:wrap-anywhere"
        ><AppIcon v-if="level === option.value" size="meta" name="check"
      /></span>
    </button>
  </div>
</template>
<script setup>
// 1. Import necessary components
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import AppIcon from '@/shared/components/AppIcon.vue';
import { MOOD_OPTIONS } from '../constants/symptom';
import MoodFace from './MoodFace.vue';
// 2. Use
const level = defineModel({ type: [Number, String], default: 0 });
const props = defineProps({
  // Options with { value, label, background, photo }; the built-in mood levels apply when omitted.
  options: { type: Array, default: null },
  stacked: { type: Boolean, default: false },
  readonly: { type: Boolean, default: false },
  faceSize: { type: Number, default: 38 },
});
const { t, tm, rt } = useI18n();
// 3. Define a reactive variable
// 4. Define methods or computed properties if needed
const items = computed(() => {
  if (props.options) {
    return props.options;
  }
  return MOOD_OPTIONS.map((option) => ({
    value: option.value,
    label: rt(tm('nkf.moodLabels')[option.value - 1]),
    background: option.background,
  }));
});
// 5. VueJS Hooks
</script>
<style scoped>
.mood-choice[aria-pressed='true'] {
  border-color: var(--border-success);
  background: var(--success-soft);
  color: var(--success);
}
.moods--stacked {
  grid-template-columns: 1fr;
  gap: 9px;
}
.moods--stacked .mood-choice {
  font: var(--type-content-strong);
  border-width: 2px;
  color: var(--foreground);
  flex-direction: row;
  gap: 14px;
  padding: 10px 14px 10px 12px;
  background: var(--mood-background);
  border-radius: var(--radius-card);
  text-align: start;
}
.moods--stacked .mood-choice[aria-pressed='true'] {
  border-color: var(--primary);
  background: var(--mood-background);
  color: var(--foreground);
}
.mood-choice:disabled {
  cursor: default;
}
.moods--stacked .mood-choice > span:first-of-type {
  flex: 1;
}
.mood-choice[aria-pressed='true'] .mood-choice__radio {
  background: var(--primary);
  border-color: var(--primary);
}
</style>
